import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Engineering Review or AWP Review — which of IQ Verity's two ways of working is on screen.
 *
 * ONE MODULE, TWO LENSES. The shell, the library and the findings pipeline are the same; what
 * changes is what a project holds. An engineering project holds deliverables, an AWP project holds
 * CWP / EWP / PWP work packages. The switch under the logo picks which projects you are looking at.
 *
 * In the URL as `?mode=awp`, for the reasons the tab is (see useNavTab): a refresh keeps you where
 * you were, and a link can send a colleague straight to the AWP side. Absent means engineering, so
 * every link made before AWP existed still lands where it did.
 *
 * Switching closes whatever was open inside a tab — an engineering project is not a place on the
 * AWP side — but keeps the tab itself.
 */
export type ReviewMode = "engineering" | "awp";

const NESTED_PARAMS = ["project", "standard"];

export function useReviewMode(): [ReviewMode, (mode: ReviewMode) => void] {
  const [params, setParams] = useSearchParams();
  const mode: ReviewMode = params.get("mode") === "awp" ? "awp" : "engineering";

  const setMode = useCallback(
    (next: ReviewMode) => {
      const out = new URLSearchParams(params);
      if (next === "awp") out.set("mode", "awp");
      else out.delete("mode");
      for (const nested of NESTED_PARAMS) out.delete(nested);
      setParams(out, { replace: true });
    },
    [params, setParams],
  );

  return [mode, setMode];
}
